import { Check, Circle } from "lucide-react";
import { useDateFormatter } from "@/utils/datetime";
import { isDone, type IntroCardStatus, type OutreachCardKey } from "../introCards/introCardStatus";

const STEP_LABEL: Record<OutreachCardKey, string> = {
  email: "Email",
  text: "Text",
  webForm: "Web form",
  followUpEmail: "Follow-up email",
  followUpText: "Follow-up text",
};

/**
 * D8 — the Broker intro tab's progress, one compact row under the readiness
 * strip: Email · Text · Web form · Follow-up email · Follow-up text, each ✓ with
 * its date once sent or logged and ○ until then.
 *
 * <p>🔴 <b>Display only.</b> It blocks nothing — outreach order stays a
 * suggestion — and it reads the SAME statuses the card pills read
 * (`introCardStatus`), so the two cannot disagree. Clicking a step opens that
 * card and scrolls to it.</p>
 */
export function ProgressStrip({
  steps,
  onSelect,
}: {
  steps: readonly { key: OutreachCardKey; status: IntroCardStatus }[];
  onSelect: (key: OutreachCardKey) => void;
}) {
  const { date } = useDateFormatter();
  return (
    <nav aria-label="Broker outreach progress" data-testid="broker-progress-strip">
      <ol className="flex flex-wrap items-center gap-x-space-3 gap-y-space-1 m-0 p-0 list-none">
        {steps.map(({ key, status }) => {
          const done = isDone(status);
          const when = status.state === "sent" || status.state === "logged" ? date(status.date) : null;
          return (
            <li key={key}>
              <button
                type="button"
                onClick={() => onSelect(key)}
                className={`inline-flex items-center gap-space-1 text-small rounded-sm focus-visible:outline-none focus-visible:shadow-goldRing ${done ? "text-status-success" : "text-gray-mid"}`}
                data-testid={`broker-progress-${key}`}
                data-done={done ? "true" : "false"}
              >
                {done ? <Check size={14} strokeWidth={2} aria-hidden /> : <Circle size={12} strokeWidth={1.75} aria-hidden />}
                <span className="text-onyx">{STEP_LABEL[key]}</span>
                {when ? <span>{when}</span> : null}
                <span className="sr-only">{done ? "done" : "not done"}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
